import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import useMediaQuery from "@mui/material/useMediaQuery";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "../../hooks";
import { DIALOG_ACTIONS_SX, DIALOG_PAPER_SX, DIALOG_TITLE_SX } from "./ui/dialogStyles";
import { formatAppointmentDate, formatAppointmentTime, formatMoney, } from "./appointmentPresentation";
const REASON_MAX_LENGTH = 500;
function RefundPreviewNote({ appointment, }) {
    const { t } = useTranslation("portal");
    const preview = appointment.refundPreview;
    if (!preview)
        return null;
    const amount = formatMoney(preview.eligibleAmount, preview.currency ?? appointment.currency);
    const percentage = preview.refundPercentage;
    if (percentage === 0 || preview.eligibleAmount === 0) {
        return (_jsx(Alert, { severity: "info", sx: { mb: 2, borderRadius: 2 }, children: t("refundNone", "This is past the clinic's refund window, so no refund is due.") }));
    }
    const channel = preview.refundChannel;
    const body = channel === "WALLET"
        ? t("refundWallet", "Your session credit will be returned to your wallet.")
        : channel === "MANUAL"
            ? t("refundManual", "You paid at the clinic, so staff will return {{amount}} to you in person.", { amount })
            : channel === "GATEWAY"
                ? t("refundGateway", "{{amount}} will be refunded to you.", { amount })
                :
                    t("refundAmountOnly", "{{amount}} is eligible for refund.", {
                        amount,
                    });
    return (_jsxs(Alert, { severity: "info", sx: { mb: 2, borderRadius: 2 }, children: [body, percentage != null ? (_jsx(Typography, { variant: "caption", sx: { display: "block", mt: 0.5, color: "text.secondary" }, children: t("refundTier", "{{percentage}}% under the clinic's policy.", {
                    percentage,
                }) })) : null] }));
}
export function CancelAppointmentDialog({ open, appointment, submitting, error, onClose, onConfirm, }) {
    const { t } = useTranslation("portal");
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const [reason, setReason] = React.useState("");
    React.useEffect(() => {
        if (open)
            setReason("");
    }, [open, appointment?.id]);
    if (!appointment)
        return null;
    const time = formatAppointmentTime(appointment);
    return (_jsxs(Dialog, { open: open, onClose: submitting ? undefined : onClose, fullWidth: true, maxWidth: "xs", fullScreen: fullScreen, slotProps: { paper: { sx: DIALOG_PAPER_SX } }, children: [_jsx(DialogTitle, { sx: DIALOG_TITLE_SX, children: t("cancelTitle", "Cancel this appointment?") }), _jsxs(DialogContent, { children: [_jsxs(Box, { sx: (muiTheme) => ({
                            mb: 2,
                            p: 1.5,
                            borderRadius: 2,
                            border: `1px solid ${muiTheme.palette.divider}`,
                            backgroundColor: alpha(muiTheme.palette.error.main, 0.04),
                        }), children: [_jsx(Typography, { variant: "subtitle2", sx: { fontWeight: 700 }, children: appointment.doctorName || "—" }), _jsx(Typography, { variant: "body2", sx: { color: "text.secondary" }, children: [formatAppointmentDate(appointment), time]
                                    .filter(Boolean)
                                    .join(" · ") })] }), _jsx(RefundPreviewNote, { appointment: appointment }), error ? (_jsx(Alert, { severity: "error", sx: { mb: 2, borderRadius: 2 }, children: error })) : null, _jsx(TextField, { fullWidth: true, multiline: true, minRows: 2, size: "small", label: t("cancelReasonLabel", "Reason (optional)"), value: reason, onChange: (event) => setReason(event.target.value.slice(0, REASON_MAX_LENGTH)), disabled: submitting, slotProps: { htmlInput: { maxLength: REASON_MAX_LENGTH } } })] }), _jsxs(DialogActions, { sx: DIALOG_ACTIONS_SX, children: [_jsx(Button, { onClick: onClose, disabled: submitting, sx: { borderRadius: 2 }, children: t("keepAppointment", "Keep it") }), _jsx(Button, { color: "error", variant: "contained", onClick: () => onConfirm(reason), disabled: submitting, sx: { borderRadius: 2 }, children: submitting ? (_jsx(CircularProgress, { size: 18, color: "inherit" })) : (t("confirmCancel", "Cancel appointment")) })] })] }));
}
